import {
    DEFAULT_LANGUAGE_CODE,
    isValidLocale,
} from 'components/LanguageSelector'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import {
    Context,
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
    useCallback
} from 'react'
import { CategoryApiOutput } from 'types/category'
import { useWindowDimensions } from 'utils/dom-helpers'
import { useApiRequest } from 'utils/fetch-helper'
import { toggleLoader } from 'utils/spinner-helper'
import { useAppErrorApiContext } from './AppErrorContext'
import { Language } from './types'

export const DEFAULT_ERROR_STATE: IError = {
    message: '',
    tag: '',
}

export const FAILED_TO_LOAD_TRANSLATION = 'Failed to load translation.'
export const LANGUAGE = 'LANGUAGE'
export const PREFERRED_LANGUAGES = 'preferred_languages'

export type Tier = {
    id: string
    name: string
}

type TranslationStruct = {
    title?: string
    [key: string]: any
}

export interface IError {
    message: string
    tag?: string
}

export interface SkyContextProps {
    translations: Record<string, string>
    setTranslations(t: Record<string, string>): void
    tiers: Tier[]
    locale: string
    loadComponentTranslations(page_name: string): void
    onLanguageChange(v: string): void
    languages: Language[]
    getLanguages(): void
    setGlobalLoading(show: boolean, blocker?: boolean): void
    integration_categories: CategoryApiOutput[]
    is_mobile: boolean
}

const SkyContext: Context<SkyContextProps> = createContext(
    {} as SkyContextProps
)

export function useAppContext() {
    return useContext(SkyContext)
}

export function SkyAppDataProvider({ children }: { children: ReactNode }) {
    const router = useRouter()
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const dimension = useWindowDimensions()

    // states
    const [tiers, setTiers] = useState([] as Tier[])
    const [integration_categories, setIntegrationCategories] = useState<CategoryApiOutput[]>([])

    const [translations_to_load, appendToTranslationLoadList] = useState<Record<string, string>>({})
    const [translation_xhr_queue, appendToTranslationLoadQueue] = useState<Record<string, Promise<TranslationStruct>>>({})
    const [translations, setTranslations] = useState<Record<string, string>>({})

    const [api_languages, setApiLanguages] = useState<Language[]>([])
    const [locale, setLocale] = useState<string>(localStorage.getItem(LANGUAGE) as string)
    const [is_mobile, toggleMobile] = useState<boolean>(false)

    useEffect(() => {
        let new_is_mobile = dimension.inner_width < 1024
        if (new_is_mobile != is_mobile) {
            toggleMobile(new_is_mobile)
        }
    }, [dimension])


    // callbacks & effects
    const getLanguages = useCallback(() => {
        getApiRequest(`/v1/languages?size=500`)
            .then(languages => setApiLanguages(languages?.content))
    }, [])

    useEffect(() => {
        getApiRequest(`/v1/tiers`).then(response => {
            if (response.error) {
                throw response
            }
            setTiers(response)
        }).catch(err => {
            console.error('Tiers API failed. We are in maintenace mode.', err)
            window.location.href = '/maintenance.html'
        })
        getLanguages()
        getApiRequest(`/v1/categories/by-type?type=INTEGRATION`).then(integrations => {
            if (integrations?.content) {
                setIntegrationCategories(integrations.content)
            }
        })

        const param_lang = router?.query?.lang as string
        if (param_lang) {
            localStorage.setItem(LANGUAGE, param_lang)
        }
        else if (locale === null || locale === undefined || locale === 'en_US') {
            localStorage.setItem(LANGUAGE, DEFAULT_LANGUAGE_CODE)
        }
    }, [])

    useEffect(() => {
        if (api_languages?.length > 0) {
            const local = localStorage.getItem(LANGUAGE) as string
            setLanguage(local)
        }
    }, [api_languages])

    useEffect(() => {
        Object.keys(translations_to_load).forEach((key: string) => {
            if (translations_to_load[key]) {
                const page_name = translations_to_load[key]
                appendToTranslationLoadList((prev_translations_to_load) => ({
                    ...prev_translations_to_load,
                    [key]: '',
                }))

                const translation_req = getApiRequest(
                    `/v1/contents?pageName=${page_name}`
                )

                appendToTranslationLoadQueue((prev_translation_xhr_queue) => ({
                    ...prev_translation_xhr_queue,
                    [key]: translation_req,
                }))
            }
        })
    }, [translations_to_load])

    useEffect(() => {
        const temp_translations: Record<string, string> = {}
        const promises = Promise.all(
            Object.keys(translation_xhr_queue).map((q) => {
                temp_translations[q] = q
                return translation_xhr_queue[q]
            })
        )

        promises.then((translations_xhrs: any[]) => {
            translations_xhrs.forEach((translations_xhr) => {
                if (translations_xhr) {
                    const xhr = translations_xhr[locale] || translations_xhr[DEFAULT_LANGUAGE_CODE]
                    if (xhr) {
                        xhr.forEach(
                            ({ key, value }: any) => {
                                temp_translations[key] = value
                            }
                        )
                        temp_translations[translations_xhr.title] = translations_xhr.title
                    }
                }
            })
            setTranslations((prev) => ({
                ...prev,
                ...temp_translations
            }))
        })
    }, [translation_xhr_queue, locale])

    useEffect(() => {
        if (!translations.COMMON_TRANSLATION_ID) {
            loadComponentTranslations('COMMON_TRANSLATION_ID')
        }
    }, [translations])

    function loadComponentTranslations(translation_key: string) {
        const page_name = getConfig().publicRuntimeConfig[translation_key]
        if (translations_to_load[translation_key] == undefined) {
            appendToTranslationLoadList((prev_translations_to_load) => ({
                ...prev_translations_to_load,
                [translation_key]: page_name,
            }))
        }
    }

    function setLanguage(lang: string): string {
        if (!isValidLocale(lang, api_languages)) {
            lang = DEFAULT_LANGUAGE_CODE
            localStorage.setItem(LANGUAGE, DEFAULT_LANGUAGE_CODE)
        }
        if (lang != locale) {
            setLocale(lang)
        }
        document.documentElement.lang = lang

        return lang
    }

    function onLanguageChange(v: string) {
        const lang = setLanguage(v)
        localStorage.setItem(LANGUAGE, lang)
        router.push({
            query: {
                ...router.query,
                lang: lang,
            },
        })
    }

    function setGlobalLoading(show: boolean, blocker = true) {
        toggleLoader(show, blocker)
    }

    let ctx: SkyContextProps = {
        tiers,
        loadComponentTranslations,
        onLanguageChange,
        translations,
        setTranslations,
        languages: api_languages,
        getLanguages,
        setGlobalLoading,
        locale,
        integration_categories,
        is_mobile
    }

    return (
        <SkyContext.Provider value={ctx}>
            {children}
        </SkyContext.Provider>
    )
}

export default SkyContext
